import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import t from 'ember-intl/helpers/t';
import { HStack, Section, VStack } from 'tio-ui/components/layout';
import AlertBanner from 'tio-ui/components/alert-banner';
import { PhRocketLaunch } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import LinkedAccountsCard from 'tio-employee/components/accounts/linked-accounts-card';
import StrategyFinder0726PlanComparison from 'tio-common/components/strategy-finder-0726/plan-comparison';
import StrategyFinder0726WhatHappensNext from 'tio-common/components/strategy-finder-0726/what-happens-next';
import StrategyFinder0726UsageHistory from 'tio-common/components/strategy-finder-0726/usage-history';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';
import type { LoanSummary } from 'tio-common/types/loan-summary';

interface S {
  Args: {
    model: {
      accounts: AccountModel[];
      loanSummary: LoanSummary;
      person: PersonModel | null;
      user: UserModel | null;
      mostRecentRecommendation: RecommendationModel | null;
      history: RecommendationModel[];
    };
  };
}

export default class RepaymentStrategyFinderIndexTemplate extends Component<S> {
  @service declare router: RouterService;

  goToForm = () => {
    this.router.transitionTo(
      'authenticated.repaying-student-debt.repayment-strategy-finder.questionnaire'
    );
  };

  <template>
    {{#if @model.person}}
      <HStack>
        <LinkedAccountsCard
          @accounts={{@model.accounts}}
          @loanSummary={{@model.loanSummary}}
          @person={{@model.person}}
          @programName={{t "strategy_finder_0726.title"}}
          @source="strategy_finder"
          @providers="nslds"
        />
        {{! A <Section>, not a <SectionPanel>, so this panel and the accounts
            panel beside it in the HStack render one title size. }}
        <Section>
          <:header>
            {{! The wrapper IS the heading and lays out as a block, so the icon
                and the title need a row of their own or the icon takes its own
                line. }}
            <span class="tio-row-tight">
              <PhRocketLaunch @size="20" class="tio-fg-brand shrink-0" />
              {{t "strategy_finder_0726.get_started_title"}}
            </span>
          </:header>
          <:body>
            <VStack>
              <p class="text-sm">{{t "strategy_finder_0726.intro"}}</p>
              <div>
                <Button @intent="primary" {{on "click" this.goToForm}} data-test-sf-0726-start>
                  {{t "strategy_finder_0726.get_started_cta"}}
                </Button>
              </div>
            </VStack>
          </:body>
        </Section>
      </HStack>

      {{#if @model.mostRecentRecommendation}}
        <VStack>
          <StrategyFinder0726PlanComparison
            @recommendation={{@model.mostRecentRecommendation}}
            @title={{t "strategy_finder_0726.previous_results"}}
          />
          <div>
            <LinkTo
              @route="authenticated.repaying-student-debt.repayment-strategy-finder.results"
              @model={{@model.mostRecentRecommendation}}
              class="app-text-link"
            >
              {{t "strategy_finder_0726.view_full_results"}}
            </LinkTo>
          </div>
          <StrategyFinder0726WhatHappensNext
            @questionnaireRoute="authenticated.repaying-student-debt.repayment-strategy-finder.questionnaire"
          />
        </VStack>
      {{/if}}

      {{#if @model.history.length}}
        <StrategyFinder0726UsageHistory
          @recommendations={{@model.history}}
          @resultsRoute="authenticated.repaying-student-debt.repayment-strategy-finder.results"
        />
      {{/if}}
    {{else}}
      <AlertBanner @intent="warning" data-test-sf-0726-no-loans>
        <p class="text-sm">{{t "strategy_finder_0726.no_loans"}}</p>
      </AlertBanner>
    {{/if}}
  </template>
}
